import { createSelector } from '@reduxjs/toolkit';
import { getTokensFromPoolDataPlaneUri } from '../../ids/pool';
import { createProjectDataPlaneUri } from '../../ids/project';
import { Status } from '../../models/common';
import { unique } from '../../utilities/array';
import { get } from '../../utilities/serializable-map';
import { statusAdapter } from '../adapters/common/status-adapter';
import { poolAdapter } from '../adapters/pool-adapters';
import { StoreState } from '../store/store-state';
import {
    StoreStateSelector,
    createGroupedMapFromEntitiesSelector,
    createMapFromEntitiesSelector,
    getStringIdsSelector,
    isStatusTerminal,
} from './common';
import { getStatusesForListSchedulesByProject } from './schedule-selectors';

/**
 * Basic selectors
 */

export const getStatusForDiscoverPoolsForProjects: StoreStateSelector<Status> = (store) =>
    store.poolStore.status.discoverPoolsForProjects;

export const getStatusForDiscoverPoolsForDevBoxes: StoreStateSelector<Status> = (store) =>
    store.poolStore.status.discoverPoolsForDevBoxes;

/**
 * Entity state selectors
 */

const poolSelectors = poolAdapter.getSelectors<StoreState>((store) => store.poolStore.data.pools);

const statusesForListPoolsSelectors = statusAdapter.getSelectors<StoreState>(
    (store) => store.poolStore.status.listPools
);

/**
 * Composed selectors
 */

export const getDataPlaneIdentifiersForPools = getStringIdsSelector(poolAdapter, (store) => store.poolStore.data.pools);

export const getPools = createMapFromEntitiesSelector(poolSelectors.selectAll);

export const getHasPools: StoreStateSelector<boolean> = createSelector(
    [poolSelectors.selectTotal],
    (count) => count > 0
);

export const getPoolsByProject = createGroupedMapFromEntitiesSelector(poolSelectors.selectAll, (entity) => {
    const { id } = entity;
    const { devCenter, projectName } = getTokensFromPoolDataPlaneUri(id);
    return createProjectDataPlaneUri({ devCenter, projectName });
});

export const getProjectUrisForPoolsRequiringSchedules: StoreStateSelector<string[]> = createSelector(
    [getDataPlaneIdentifiersForPools, getStatusesForListSchedulesByProject],
    (ids, statusesForListSchedulesByProject) => {
        const projectIds = unique(
            ids.map((id) => {
                const { devCenter, projectName } = getTokensFromPoolDataPlaneUri(id);
                return createProjectDataPlaneUri({ devCenter, projectName });
            })
        );

        return projectIds.filter((projectId) => !isStatusTerminal(get(statusesForListSchedulesByProject, projectId)));
    }
);

export const getStatusesForListPools = createMapFromEntitiesSelector(statusesForListPoolsSelectors.selectAll);
